import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  IActionDealHistory,
  IApplicants,
  IContractCredentials,
  IDealByID,
  IDealsPagination,
  IDiscount,
  IEnrollPresentationDataPagination,
  IGetTokenForLeadForm,
  ILeadData,
  ILeadDocument,
  ILeadPagination,
  ILeadQRRegisterMutation,
  ILeadStatuses,
  IPaymentFullHistory,
  IPayments,
  IPresentationInfo,
  ITaskFile,
  ITaskFull,
  ITaskWithPagination,
} from '@interfaces/ISales';
import { IUsersPagination } from '@interfaces/IUser';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SalesViewService {
  constructor(private _http: HttpClient) {}

  getTokenForLeadForm(queries?: Params): Observable<IGetTokenForLeadForm> {
    let url = `${environment.baseUrl}api/v1/sales/leads/filling-token-generate/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<IGetTokenForLeadForm>(url);
  }

  getPresentationInfo(): Observable<IPresentationInfo> {
    const url = `${environment.baseUrl}api/v1/sales/presentation-info/`;
    return this._http.get<IPresentationInfo>(url);
  }

  getEnrollees(queries?: Params): Observable<IUsersPagination> {
    let url = `${environment.baseUrl}api/v1/sales/enrollees/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<IUsersPagination>(url);
  }

  getEnrolleesInPresentation(
    queries?: Params
  ): Observable<IEnrollPresentationDataPagination> {
    let url = `${environment.baseUrl}api/v1/sales/enrollees/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<IEnrollPresentationDataPagination>(url);
  }

  getLeads(queries?: Params): Observable<ILeadPagination> {
    let url = `${environment.baseUrl}api/v1/sales/leads/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<ILeadPagination>(url);
  }

  getLeadInQR(queries?: Params): Observable<ILeadQRRegisterMutation> {
    let url = `${environment.baseUrl}api/v1/sales/leads/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<ILeadQRRegisterMutation>(url);
  }

  getLead(id: number, queries?: Params): Observable<ILeadData> {
    let url = `${environment.baseUrl}api/v1/sales/leads/${id}/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<ILeadData>(url);
  }

  getLeadStatuses(): Observable<ILeadStatuses[]> {
    const url = `${environment.baseUrl}api/v1/sales/lead-statuses/`;
    return this._http.get<ILeadStatuses[]>(url);
  }

  getTasks(queries?: Params): Observable<ITaskWithPagination> {
    let url = `${environment.baseUrl}api/v1/sales/tasks/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<ITaskWithPagination>(url);
  }

  getTask(id: number): Observable<ITaskFull> {
    const url = `${environment.baseUrl}api/v1/sales/tasks/${id}/`;
    return this._http.get<ITaskFull>(url);
  }

  getDealHistoryById(
    deal_id: number
  ): Observable<{ results: IActionDealHistory[] }> {
    const url = `${environment.baseUrl}api/v1/accountants/deal-discount/${deal_id}/history/`;
    return this._http.get<{ results: IActionDealHistory[] }>(url);
  }

  getTaskFiles(queries?: Params): Observable<ITaskFile[]> {
    let url = `${environment.baseUrl}api/v1/sales/task-files/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<ITaskFile[]>(url);
  }

  getContractCredentials(): Observable<IContractCredentials[]> {
    const url = `${environment.baseUrl}api/v1/sales/contract-credentials/`;
    return this._http.get<IContractCredentials[]>(url);
  }

  getLeadDocuments(id: number): Observable<ILeadDocument[]> {
    const url = `${environment.baseUrl}api/v1/sales/lead-documents/?lead_id=${id}`;
    return this._http.get<ILeadDocument[]>(url);
  }

  getLeadContract(lead_id: number): Observable<string> {
    const url = `${environment.baseUrl}api/v1/sales/lead/${lead_id}/contract/`;
    return this._http.get<string>(url);
  }

  getAllSalesDeals(queries?: Params): Observable<IDealsPagination> {
    let url = `${environment.baseUrl}api/v1/sales/deals/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }

    return this._http.get<IDealsPagination>(url);
  }

  getOneDealById(deal_id: number): Observable<IDealByID> {
    const url = `${environment.baseUrl}api/v1/sales/deals/${deal_id}/`;
    return this._http.get<IDealByID>(url);
  }

  getAllPayments(queries?: Params): Observable<IPayments> {
    let url = `${environment.baseUrl}api/v1/accountants/payments/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.get<IPayments>(url);
  }

  getFullViewOfPayment(payment_id: number): Observable<IPaymentFullHistory> {
    const url = `${environment.baseUrl}api/v1/accountants/payments/${payment_id}/`;
    return this._http.get<IPaymentFullHistory>(url);
  }

  getAllDiscounts(): Observable<IDiscount[]> {
    const url = `${environment.baseUrl}api/v1/accountants/discounts/`;
    return this._http.get<IDiscount[]>(url);
  }

  getAllApplicants(): Observable<IApplicants> {
    const url = `${environment.baseUrl}api/v1/sales/applicants/`;
    return this._http.get<IApplicants>(url);
  }
}
